import type { PreviewGame } from "@/features/casino/components/GamesPreviewRow"

export const lobbyChipIds = [
  "originals",
  "slots",
  "blackjack",
  "live",
  "popular"
] as const

export type LobbyChipId = (typeof lobbyChipIds)[number]

export const lobbyRailIds = [
  "originals",
  "winningNow",
  "slots",
  "blackjack",
  "live"
] as const

export type LobbyRailId = (typeof lobbyRailIds)[number]

export const defaultLobbyChip: LobbyChipId | null = null

const chipRails: Record<LobbyChipId, readonly [LobbyRailId, ...LobbyRailId[]]> =
  {
    originals: ["originals"],
    slots: ["slots"],
    blackjack: ["blackjack"],
    live: ["live"],
    popular: ["winningNow"]
  }

export function railsForChip(chip: LobbyChipId | null): readonly LobbyRailId[] {
  if (!chip) {
    return lobbyRailIds
  }

  return chipRails[chip]
}

export type LobbyFilterId = "providers" | "sort"

export const mockProviderIds = [
  "felt",
  "habanero",
  "gameArt",
  "gamingCorp"
] as const

export type MockProviderId = (typeof mockProviderIds)[number]

export const providerFilterValues = [
  "felt",
  "habanero",
  "gameArt",
  "gamingCorp",
  "pragmatic",
  "evolution",
  "playtech",
  "netent"
] as const

export type ProviderFilterValue = (typeof providerFilterValues)[number]

export const sortFilterValues = [
  "newest",
  "oldest",
  "popularity",
  "currentPlayers"
] as const

export type SortFilterValue = (typeof sortFilterValues)[number]

export const defaultSelectedProviders: readonly ProviderFilterValue[] = []

export const mockProviderGameCounts: Record<ProviderFilterValue, number> = {
  felt: 42,
  habanero: 28,
  gameArt: 15,
  gamingCorp: 9,
  pragmatic: 64,
  evolution: 31,
  playtech: 22,
  netent: 17
}

export const providerFilterPlaceholderLogo =
  "/img/casino/providers/placeholder.png"

export const defaultSortFilter: SortFilterValue | null = null

export function isLobbyChipId(value: string): value is LobbyChipId {
  return lobbyChipIds.some((id) => id === value)
}

export function isProviderFilterValue(
  value: string
): value is ProviderFilterValue {
  return providerFilterValues.some((id) => id === value)
}

export function isSortFilterValue(value: string): value is SortFilterValue {
  return sortFilterValues.some((id) => id === value)
}

export const previewRailGameCount = 12
export const filteredGamesPageSize = 18
export const mockFilteredGameCount = 100

const railTitles: Record<LobbyRailId, readonly [string, ...string[]]> = {
  originals: [
    "Sweet Candy Cash",
    "Stampeding Strike",
    "Sweet Candy Cash Megaways"
  ],
  winningNow: ["Robbin Robin", "Dwarven Gems Megaways", "Vegas Branded"],
  slots: ["Robbin Robin", "Dwarven Gems Megaways", "Vegas Branded"],
  blackjack: ["Blackjack", "Blackjack Live", "Blackjack VIP"],
  live: ["Roulette Live", "Blackjack Live", "Baccarat Live"]
}

function mockGame(id: string, title: string): PreviewGame {
  return {
    identifier: id,
    title,
    producer: "Iron Dog",
    provider: "irondog",
    currency: "HNL",
    released_at: null
  }
}

function mockRail(
  prefix: string,
  titles: readonly [string, ...string[]],
  count: number
): PreviewGame[] {
  return Array.from({ length: count }, (_, index) => {
    return mockGame(
      `${prefix}-${index + 1}`,
      titles[index % titles.length] ?? titles[0]
    )
  })
}

export const mockRailGames: Record<LobbyRailId, PreviewGame[]> = {
  originals: mockRail(
    "mock-originals",
    railTitles.originals,
    previewRailGameCount
  ),
  winningNow: mockRail(
    "mock-winning",
    railTitles.winningNow,
    previewRailGameCount
  ),
  slots: mockRail("mock-slots", railTitles.slots, previewRailGameCount),
  blackjack: mockRail(
    "mock-blackjack",
    railTitles.blackjack,
    previewRailGameCount
  ),
  live: mockRail("mock-live", railTitles.live, previewRailGameCount)
}

export function railForChip(chip: LobbyChipId): LobbyRailId {
  return chipRails[chip][0]
}

export function mockFilteredGames(railId: LobbyRailId): PreviewGame[] {
  return mockRail(
    `filtered-${railId}`,
    railTitles[railId],
    mockFilteredGameCount
  )
}

export function visibleFilteredCount(
  page: number,
  total: number,
  pageSize = filteredGamesPageSize
) {
  if (page < 1) {
    return Math.min(pageSize, total)
  }

  return Math.min(page * pageSize, total)
}

export const casinoSeeAllHref = "/casino"
